import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { Deactivation } from '../interface/Deactivation.interface';
import { DIALOG_MESSAGE } from '@app/constants/Message.constant';
import { ConfirmDialogComponent }
    from '@app/secure/share/confirmDialog/confirmDialog.component';

@Injectable({
    providedIn: 'root',
})
/**
 * @desc   - Route guard helper for components that implement Deactivation
 */
class ComponentGuard 
{
    private dialogModel = inject(MatDialog);

    /**
     * @desc   - Evaluates whether navigation away from a component is allowed
     * @access - public
     * @param  - component : component implementing Deactivation
     * @return - boolean, Observable, or Promise indicating whether to leave
     */
    canDeactivate
    (
        component: Deactivation
    ): Observable<boolean> | boolean 
    {
        let result: Observable<boolean> | boolean = true;
    
        if (component.canDeactivate) 
        {
            const canDeactivateResult = component.canDeactivate();
    
            if (typeof canDeactivateResult === 'boolean') 
            {
                result = canDeactivateResult ? true : this.showConfirmDialog();
            } 
            else if (canDeactivateResult instanceof Observable) 
            {
                result = canDeactivateResult;
            } 
            else if (canDeactivateResult instanceof Promise) 
            {
                // Convert Promise to Observable
                result = new Observable<boolean>(observer => 
                {
                    canDeactivateResult.then(
                        (value: any) => 
                        {
                            observer.next(value);
                            observer.complete();
                        },
                        (error: any) => observer.error(error)
                    );
                });
            }
        }
    
        return result;
    }    

    /**
     * @desc   - Opens unsaved-changes confirm dialog for route deactivation
     * @access - private
     * @param  -
     * @return - Observable that emits true when user confirms leaving
     */
    private showConfirmDialog(): Observable<boolean> 
    {
        let dialogRef;

        dialogRef = this.dialogModel.open(ConfirmDialogComponent,
        {
            width: '360px',
            data:
            {
                title: 'Save Data',
                message: DIALOG_MESSAGE.UNSAVED_CHANGES
            }
        });

        return dialogRef.afterClosed();
    }
}

/**
 * @desc   - Functional canDeactivate guard for Angular route configuration
 * @access - public
 * @param  - component : component implementing Deactivation
 * @return - boolean or Observable from ComponentGuard.canDeactivate
 */
const canDeactivateGuard = (component: Deactivation) => 
{
    return inject(ComponentGuard).canDeactivate(component);
};

export { ComponentGuard, canDeactivateGuard };